import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getActiveModelStatusGrpc,
  type ActiveModelStatus,
} from '../../../shared/api/grpc/revitClient';

export interface UseQuickFillModelStatusOptions {
  projectKey: string | undefined;
}

export interface UseQuickFillModelStatusResult {
  activeModelStatus: ActiveModelStatus | null;
  isCentralInaccessible: boolean;
  isModelWriteBlocked: boolean;
  modelBlockReason: 'no-document' | 'central-inaccessible' | 'read-only' | null;
  modelStatusDialogDismissed: boolean;
  setModelStatusDialogDismissed: (value: boolean) => void;
  refreshActiveModelStatus: () => Promise<void>;
}

const PROBE_INTERVAL_MS = 15_000;

export function useQuickFillModelStatus(
  options: UseQuickFillModelStatusOptions
): UseQuickFillModelStatusResult {
  const { projectKey } = options;
  const [activeModelStatus, setActiveModelStatus] = useState<ActiveModelStatus | null>(null);
  // Tracks whether the user closed the blocking modal. The modal still shows
  // up on every fresh probe (mount / Recheck), but once dismissed the user
  // sees the page underneath with the inline reminder banner. All write
  // actions stay disabled regardless of dismissal — see isModelWriteBlocked.
  const [modelStatusDialogDismissed, setModelStatusDialogDismissed] = useState(false);

  const refreshActiveModelStatus = useCallback(async () => {
    const status = await getActiveModelStatusGrpc();
    setActiveModelStatus(status);
    setModelStatusDialogDismissed(false);
  }, []);

  // Probe on mount and whenever the active Revit project switches.
  // activeDocumentInfo already subscribes to Revit's document push events
  // with a 60s fallback poll (see useActiveDocumentName), so projectKey
  // transitions land here naturally. Without this hook the user could switch
  // from a writable project to a ReadOnly one without UI noticing — stale
  // state hides the blocker.
  useEffect(() => {
    void refreshActiveModelStatus();
  }, [projectKey, refreshActiveModelStatus]);

  // Belt-and-suspenders fallback poll on a tighter interval than the
  // document-name watcher (60s). 15s catches edge cases where the file
  // attribute changed mid-session (user cleared OS read-only, saved-as
  // a writable copy, etc.) without a project switch.
  useEffect(() => {
    const timer = window.setInterval(() => {
      void refreshActiveModelStatus();
    }, PROBE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refreshActiveModelStatus]);

  // Second blocker: a workshared LOCAL copy whose central model cannot be
  // reached — central file missing on disk, or no central path resolvable.
  // Revit fails the per-element check-out with "The Central Model is
  // inaccessible", so QuickFill must not even start. Detached models ARE
  // editable (changes stay in the local copy) and cloud-hosted central models
  // are managed by Autodesk, so both are excluded here — the backend reports
  // centralModelExists = true for cloud (see RevitConnector.GetActiveModelStatus).
  const isCentralInaccessible = useMemo(
    () =>
      Boolean(
        activeModelStatus &&
          activeModelStatus.success &&
          activeModelStatus.isWorkshared &&
          !activeModelStatus.isDetached &&
          !activeModelStatus.centralModelExists
      ),
    [activeModelStatus]
  );

  // Hard-block writes ONLY on Document.IsReadOnly — that is Revit's own
  // canonical "transactions will fail on this document" flag. We deliberately
  // do NOT block on IsReadOnlyFile (OS attribute can be set on .rvt files
  // Revit still happily writes to via Save-As / workshared sync; false
  // positives common for Autodesk-shipped samples that Revit treats as
  // a writable cloned copy) or !IsModifiable (transient false during model
  // regeneration / mid-transaction; flickers on a healthy doc). Worksharing
  // is also NOT a blocker — workshared+writable models are fine; soft warn
  // for detached / missing-central only.
  const isModelWriteBlocked = useMemo(
    () =>
      Boolean(
        activeModelStatus &&
          activeModelStatus.success &&
          (!activeModelStatus.hasActiveDocument ||
            activeModelStatus.isReadOnly ||
            isCentralInaccessible)
      ),
    [activeModelStatus, isCentralInaccessible]
  );

  // Which specific reason is blocking — drives the dialog/banner copy so the
  // user gets a precise explanation and concrete recovery steps.
  const modelBlockReason: UseQuickFillModelStatusResult['modelBlockReason'] = useMemo(
    () =>
      !activeModelStatus || !activeModelStatus.success || !isModelWriteBlocked
        ? null
        : !activeModelStatus.hasActiveDocument
          ? 'no-document'
          : isCentralInaccessible
            ? 'central-inaccessible'
            : 'read-only',
    [activeModelStatus, isCentralInaccessible, isModelWriteBlocked]
  );

  return {
    activeModelStatus,
    isCentralInaccessible,
    isModelWriteBlocked,
    modelBlockReason,
    modelStatusDialogDismissed,
    setModelStatusDialogDismissed,
    refreshActiveModelStatus,
  };
}
